/**
 * Create / edit an Area-News item, scoped to a neighborhood. Reuses the
 * existing admin area-news endpoints via neighborhoodService.
 */

import { useState } from 'react';
import {
  createAreaNews,
  updateAreaNews,
  NEWS_CATEGORIES,
  NEWS_IMPACTS,
  NEWS_SEVERITIES,
  NEWS_STATUSES,
  type AreaNewsItem,
} from '../../../services/neighborhoodService';

interface Props {
  cityId: string;
  areaId: string;
  neighborhoodName: string;
  existing: AreaNewsItem | null;
  onClose: () => void;
  onSaved: () => void;
}

function toDateInput(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export default function NewsFormModal({ cityId, areaId, neighborhoodName, existing, onClose, onSaved }: Props) {
  const [titleEn, setTitleEn] = useState(existing?.titleEn || '');
  const [titleAr, setTitleAr] = useState(existing?.titleAr || '');
  const [bodyEn, setBodyEn] = useState(existing?.bodyEn || '');
  const [bodyAr, setBodyAr] = useState(existing?.bodyAr || '');
  const [category, setCategory] = useState(existing?.category || 'CONSTRUCTION');
  const [impact, setImpact] = useState(existing?.impact || 'NEUTRAL');
  const [severity, setSeverity] = useState(existing?.severity || '');
  const [status, setStatus] = useState(existing?.status || 'DRAFT');
  const [startDate, setStartDate] = useState(toDateInput(existing?.startDate) || toDateInput(new Date().toISOString()));
  const [endDate, setEndDate] = useState(toDateInput(existing?.endDate));
  const [source, setSource] = useState(existing?.source || '');
  const [sourceUrl, setSourceUrl] = useState(existing?.sourceUrl || '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!titleEn.trim() || !titleAr.trim()) { setError('Both English and Arabic titles are required.'); return; }
    if (!startDate) { setError('Start date is required.'); return; }
    if (endDate && endDate < startDate) { setError('End date must be on or after the start date.'); return; }

    const payload = {
      cityId,
      areaId,
      neighborhood: neighborhoodName,
      titleEn: titleEn.trim(),
      titleAr: titleAr.trim(),
      bodyEn: bodyEn.trim() || null,
      bodyAr: bodyAr.trim() || null,
      category,
      impact,
      severity: severity || null,
      status,
      startDate: new Date(startDate).toISOString(),
      endDate: endDate ? new Date(endDate).toISOString() : null,
      source: source.trim() || null,
      sourceUrl: sourceUrl.trim() || null,
    };

    setSaving(true);
    try {
      if (existing) await updateAreaNews(existing.id, payload);
      else await createAreaNews(payload);
      onSaved();
    } catch {
      setError('Could not save. Check the fields and try again.');
    } finally {
      setSaving(false);
    }
  };

  const field = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#B7DDD7]';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">{existing ? 'Edit news' : 'Add news'} · <span className="text-gray-400 font-normal">{neighborhoodName}</span></h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100"><i className="ri-close-line text-lg" /></button>
        </div>

        <div className="p-5 space-y-3">
          {error && <div className="rounded-lg bg-red-50 text-red-600 text-sm px-3 py-2">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">Title (English)</label>
              <input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} className={field} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Title (Arabic)</label>
              <input value={titleAr} onChange={(e) => setTitleAr(e.target.value)} dir="rtl" className={field} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">Body (English)</label>
              <textarea value={bodyEn} onChange={(e) => setBodyEn(e.target.value)} rows={3} className={field} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Body (Arabic)</label>
              <textarea value={bodyAr} onChange={(e) => setBodyAr(e.target.value)} rows={3} dir="rtl" className={field} />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-500">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={field}>
                {NEWS_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Impact</label>
              <select value={impact} onChange={(e) => setImpact(e.target.value)} className={field}>
                {NEWS_IMPACTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Severity</label>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)} className={field}>
                <option value="">-</option>
                {NEWS_SEVERITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={field}>
                {NEWS_STATUSES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">Start date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={field} />
            </div>
            <div>
              <label className="text-xs text-gray-500">End date (optional)</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={field} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">Source (optional)</label>
              <input value={source} onChange={(e) => setSource(e.target.value)} className={field} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Source URL (optional)</label>
              <input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://…" className={field} />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 rounded-lg bg-[#004D40] text-white text-sm hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">
            {saving && <i className="ri-loader-4-line animate-spin" />}
            {existing ? 'Save changes' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
